import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../capitalExpenditure/capitalExpenditure.module.scss';
import CapitalExpenditureHeader from '../capitalExpenditure/components/CapitalExpenditureHeader';
import InputField from '../capitalExpenditure/components/InputField';
import { parseNumericValue } from '../capitalExpenditure/utils/numberUtils';
import {
  buildFixedBudgetTemplateRows,
  inferBudgetItemTypeFromCode,
  sortBudgetRowsByCode as sortBudgetRowsByFixedCode,
  toCanonicalBudgetCode
} from './budgetTemplate';
import {
  normalizeBudgetFiscalYears,
  normalizeBudgetSubmissionFiscalYear,
  keepOrDefaultFiscalYear
} from './fiscalYearUtils';

const getApiBaseUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('localhost');

  const isProduction = window.location.hostname.includes('cloudlab.com.np') ||
    window.location.hostname.includes('muaccount');

  const savedUrl = localStorage.getItem('apiBaseUrl');
  if (savedUrl) {
    return savedUrl;
  }

  if (isLocalhost) {
    return 'http://localhost:8000';
  }

  if (isProduction) {
    return 'https://muaccount.api.cloudlab.com.np';
  }

  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();
const INSTITUTIONS_API = `${API_BASE_URL}/institutions`;
const FISCAL_YEARS_API = `${API_BASE_URL}/fiscal-years`;
const MASTER_ITEMS_API = `${API_BASE_URL}/master-expenditure-items/`;
const GET_BUDGET_API = `${API_BASE_URL}/get-budget-by-fiscal-year/`;
const SAVE_BUDGET_API = `${API_BASE_URL}/budgets/bulk-create/`;
const BUDGET_MAIN_DRAFT_PREFIX = 'budget_main_draft_v1';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Token ${token}` })
  };
};

const inferItemTypeFromCode = (codeNumber, explicitType = '') => (
  inferBudgetItemTypeFromCode(codeNumber, explicitType)
);

const createEmptyRow = (id) => ({
  id,
  codeNumber: '',
  expenseTitle: '',
  estimatedBudget: '',
  kaifiyat: '',
  itemType: 'capital'
});

const createDefaultRows = () => {
  const templateRows = buildFixedBudgetTemplateRows();
  return templateRows.map((item, index) => ({
    ...createEmptyRow(index + 1),
    codeNumber: item?.code_number || '',
    expenseTitle: item?.expense_title || '',
    itemType: item?.item_type || inferItemTypeFromCode(item?.code_number)
  }));
};

const mapBudgetItemsToRows = (items = []) => (
  items.map((item, index) => ({
    id: item.id || index + 1,
    codeNumber: item.code_number || '',
    expenseTitle: item.expense_title || '',
    estimatedBudget: item.estimated_budget || '',
    kaifiyat: item.remarks || '',
    itemType: inferItemTypeFromCode(item.code_number)
  }))
);

const mapTemplateItemsToRows = (items = []) => (
  buildFixedBudgetTemplateRows(items).map((item, index) => ({
    id: index + 1,
    codeNumber: item?.code_number || '',
    expenseTitle: item?.expense_title || '',
    estimatedBudget: '',
    kaifiyat: '',
    itemType: item?.item_type || inferItemTypeFromCode(item?.code_number)
  }))
);

const ensureMinimumRows = (inputRows = []) => {
  const normalizedRows = Array.isArray(inputRows)
    ? inputRows.map((row) => ({
        ...row,
        itemType: row.itemType || inferItemTypeFromCode(row.codeNumber)
      }))
    : [];

  if (normalizedRows.length === 0) {
    return createDefaultRows();
  }

  return normalizedRows;
};

const mergeRowsWithTemplateRows = (inputRows = [], templateRows = []) => {
  const normalizedRows = ensureMinimumRows(inputRows);
  if (!Array.isArray(templateRows) || templateRows.length === 0) {
    return normalizedRows;
  }

  const normalizedTemplates = ensureMinimumRows(templateRows);
  const inputByCode = new Map(
    normalizedRows
      .filter((row) => String(row?.codeNumber || '').trim())
      .map((row) => [toCanonicalBudgetCode(row.codeNumber), row])
  );

  const mergedRows = normalizedTemplates.map((templateRow, index) => {
    const canonicalCode = toCanonicalBudgetCode(templateRow.codeNumber);
    const matchedRow = canonicalCode ? inputByCode.get(canonicalCode) : null;
    return {
      id: templateRow.id || index + 1,
      codeNumber: templateRow.codeNumber || matchedRow?.codeNumber || '',
      expenseTitle: templateRow.expenseTitle || matchedRow?.expenseTitle || '',
      estimatedBudget: matchedRow?.estimatedBudget ?? '',
      kaifiyat: matchedRow?.kaifiyat || '',
      itemType: templateRow.itemType || inferItemTypeFromCode(templateRow.codeNumber)
    };
  });

  const templateCodes = new Set(
    normalizedTemplates.map((row) => toCanonicalBudgetCode(row?.codeNumber || '')).filter(Boolean)
  );
  const customRows = normalizedRows.filter((row) => {
    const canonicalCode = toCanonicalBudgetCode(row?.codeNumber || '');
    if (!canonicalCode) {
      return Boolean(
        String(row?.codeNumber || '').trim() ||
        String(row?.expenseTitle || '').trim() ||
        parseNumericValue(row?.estimatedBudget) > 0 ||
        String(row?.kaifiyat || '').trim()
      );
    }
    return !templateCodes.has(canonicalCode);
  });

  return [...mergedRows, ...customRows];
};

const sortBudgetRowsByCode = (rows = []) => (
  sortBudgetRowsByFixedCode(rows, (row) => row?.codeNumber || '')
);

const createBudgetMainDraftKey = ({
  institutionId,
  fiscalYearId,
  sourceToken,
  budgetToken,
  fromTokenSearch
}) => {
  if (!institutionId || !fiscalYearId) {
    return '';
  }

  const modePart = fromTokenSearch ? 'token' : 'auth';
  const rawTokenPart = fromTokenSearch ? (sourceToken || budgetToken || 'direct') : 'direct';
  const tokenPart = String(rawTokenPart).trim().toUpperCase() || 'direct';
  return [
    BUDGET_MAIN_DRAFT_PREFIX,
    modePart,
    institutionId,
    fiscalYearId,
    tokenPart
  ].join(':');
};

const hasBudgetSubmissionContext = (state) => (
  Boolean(
    state?.fromTokenSearch &&
    state?.tokenData?.budget_submission &&
    state?.tokenData?.budget_submission?.institution &&
    state?.tokenData?.budget_submission?.fiscal_year
  )
);

const getStoredBudgetTokenState = () => {
  try {
    const rawState = sessionStorage.getItem('budgetTokenSearchState');
    if (!rawState) {
      return null;
    }

    const parsedState = JSON.parse(rawState);
    if (hasBudgetSubmissionContext(parsedState)) {
      return parsedState;
    }
    return null;
  } catch (error) {
    console.error('Invalid stored budget token state:', error);
    return null;
  }
};

const BudgetMain = ({ onLogout, isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasAuthToken = Boolean(localStorage.getItem('authToken'));
  const routeBudgetTokenState = hasBudgetSubmissionContext(location.state) ? {
    fromTokenSearch: true,
    accessType: location.state?.accessType || 'view',
    tokenData: location.state.tokenData
  } : null;
  const storedBudgetTokenState = (
    !routeBudgetTokenState && !hasAuthToken && !isAuthenticated
  ) ? getStoredBudgetTokenState() : null;
  const effectiveBudgetTokenState = routeBudgetTokenState || storedBudgetTokenState;
  const tokenSearchBudgetSubmission = normalizeBudgetSubmissionFiscalYear(
    effectiveBudgetTokenState?.tokenData?.budget_submission || null
  );
  const tokenBudgetItemsCount = tokenSearchBudgetSubmission?.budget_items?.length || 0;
  const tokenAccessType = effectiveBudgetTokenState?.accessType || null;
  const initialSourceToken = effectiveBudgetTokenState?.tokenData?.source_token || '';
  const tokenFallbackRows = mapTemplateItemsToRows(
    effectiveBudgetTokenState?.tokenData?.fallback_items || []
  );
  const initialTokenRows = mergeRowsWithTemplateRows(
    mapBudgetItemsToRows(tokenSearchBudgetSubmission?.budget_items || []),
    tokenFallbackRows
  );

  const [rows, setRows] = useState(() => initialTokenRows);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(() => tokenAccessType === 'view');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showOfficeDropdown, setShowOfficeDropdown] = useState(false);
  const [officeSearchTerm, setOfficeSearchTerm] = useState('');
  const [officeOptions, setOfficeOptions] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(() => (
    tokenSearchBudgetSubmission
      ? {
          id: tokenSearchBudgetSubmission.institution,
          name: tokenSearchBudgetSubmission.institution_name || ''
        }
      : { id: null, name: '' }
  ));
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(() => (
    tokenSearchBudgetSubmission
      ? {
          id: tokenSearchBudgetSubmission.fiscal_year,
          name: tokenSearchBudgetSubmission.fiscal_year_display || ''
        }
      : null
  ));
  const [lastSavedToken, setLastSavedToken] = useState(() => (
    tokenSearchBudgetSubmission?.token
      ? {
          token: tokenSearchBudgetSubmission.token,
          institution: tokenSearchBudgetSubmission.institution_name || '',
          createdAt: tokenSearchBudgetSubmission.updated_at
            ? new Date(tokenSearchBudgetSubmission.updated_at).toLocaleString('ne-NP')
            : new Date().toLocaleString('ne-NP')
        }
      : null
  ));
  const [showTokenPopup, setShowTokenPopup] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loadedFromTokenSearch, setLoadedFromTokenSearch] = useState(Boolean(tokenSearchBudgetSubmission));
  const [activeBudgetToken, setActiveBudgetToken] = useState(tokenSearchBudgetSubmission?.token || '');
  const [activeSourceToken, setActiveSourceToken] = useState(initialSourceToken);
  const restoredDraftKeysRef = useRef(new Set());
  const skipNextDraftSaveRef = useRef(false);

  const selectedOfficeId = selectedOffice?.id || null;
  const selectedFiscalYearId = selectedFiscalYear?.id || null;
  const currentBudgetDraftKey = useMemo(() => createBudgetMainDraftKey({
    institutionId: selectedOfficeId,
    fiscalYearId: selectedFiscalYearId,
    sourceToken: activeSourceToken,
    budgetToken: activeBudgetToken,
    fromTokenSearch: loadedFromTokenSearch
  }), [
    selectedOfficeId,
    selectedFiscalYearId,
    activeSourceToken,
    activeBudgetToken,
    loadedFromTokenSearch
  ]);

  useEffect(() => {
    const hasStoredTokenAccess = (
      sessionStorage.getItem('allowBudgetTokenAccess') === 'true' &&
      Boolean(getStoredBudgetTokenState())
    );
    const hasValidAccess = (
      hasAuthToken ||
      isAuthenticated ||
      hasStoredTokenAccess ||
      Boolean(tokenSearchBudgetSubmission?.institution && tokenSearchBudgetSubmission?.fiscal_year)
    );

    if (!hasValidAccess) {
      sessionStorage.removeItem('allowBudgetTokenAccess');
      sessionStorage.removeItem('budgetTokenSearchState');
      navigate('/token-search', { replace: true });
    }
  }, [hasAuthToken, isAuthenticated, navigate, tokenSearchBudgetSubmission]);

  const filteredOfficeOptions = useMemo(() => (
    officeOptions.filter((office) =>
      office.name.toLowerCase().includes(officeSearchTerm.toLowerCase())
    )
  ), [officeOptions, officeSearchTerm]);

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const fetchOfficeOptions = useCallback(async () => {
    try {
      const response = await fetch(INSTITUTIONS_API);
      const data = await response.json();
      if (data.status === 'success' && Array.isArray(data.data)) {
        setOfficeOptions(data.data);
      }
    } catch (error) {
      console.error('Error loading offices:', error);
    }
  }, []);

  const fetchFiscalYears = useCallback(async () => {
    try {
      const response = await fetch(FISCAL_YEARS_API, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success' && Array.isArray(data.data)) {
        const normalizedFiscalYears = normalizeBudgetFiscalYears(data.data);
        setFiscalYears(normalizedFiscalYears);
        setSelectedFiscalYear((prevSelectedFiscalYear) => (
          keepOrDefaultFiscalYear(normalizedFiscalYears, prevSelectedFiscalYear)
        ));
      }
    } catch (error) {
      console.error('Error loading fiscal years:', error);
    }
  }, []);

  const fetchMasterItems = useCallback(async ({ force = false } = {}) => {
    if (loadedFromTokenSearch && !force) {
      return;
    }

    try {
      const response = await fetch(MASTER_ITEMS_API, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
      if (result.status === 'success' && Array.isArray(result.data)) {
        const mappedRows = mapTemplateItemsToRows(result.data);
        setRows((prevRows) => (
          force
            ? mergeRowsWithTemplateRows(prevRows, mappedRows)
            : ensureMinimumRows(mappedRows)
        ));
      }
    } catch (error) {
      console.error('Error loading master items:', error);
    }
  }, [loadedFromTokenSearch]);

  const loadExistingBudgetData = useCallback(async () => {
    if (!selectedOffice?.id || !selectedFiscalYear?.id) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${GET_BUDGET_API}?fiscal_year_id=${selectedFiscalYear.id}&institution_id=${selectedOffice.id}`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result.status !== 'success') {
        return;
      }

      const items = result?.data?.budget_items || [];
      if (items.length > 0) {
        setRows(ensureMinimumRows(mapBudgetItemsToRows(items)));
      }

      if (result?.data?.token) {
        setActiveBudgetToken(result.data.token);
        setLastSavedToken({
          token: result.data.token,
          institution: selectedOffice.name,
          createdAt: new Date().toLocaleString('ne-NP')
        });
      }
    } catch (error) {
      console.error('Error loading budget data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOffice, selectedFiscalYear]);

  useEffect(() => {
    if (hasBudgetSubmissionContext(location.state)) {
      try {
        sessionStorage.setItem('allowBudgetTokenAccess', 'true');
        sessionStorage.setItem('budgetTokenSearchState', JSON.stringify({
          fromTokenSearch: true,
          accessType: location.state?.accessType || 'view',
          tokenData: location.state.tokenData
        }));
      } catch (error) {
        console.error('Failed to persist budget token state:', error);
      }
    }
  }, [location.state]);

  useEffect(() => {
    fetchOfficeOptions();
    fetchFiscalYears();
    if (!loadedFromTokenSearch) {
      fetchMasterItems();
      return;
    }

    if (tokenBudgetItemsCount === 0) {
      fetchMasterItems({ force: true });
    }
  }, [
    fetchOfficeOptions,
    fetchFiscalYears,
    fetchMasterItems,
    loadedFromTokenSearch,
    tokenBudgetItemsCount
  ]);

  useEffect(() => {
    if (!loadedFromTokenSearch) {
      loadExistingBudgetData();
    }
  }, [loadExistingBudgetData, loadedFromTokenSearch]);

  useEffect(() => {
    if (location.state?.fromTokenSearch && location.state?.tokenData?.budget_submission) {
      const { tokenData: searchedData, accessType } = location.state;
      const budgetSubmission = normalizeBudgetSubmissionFiscalYear(searchedData.budget_submission);

      setSelectedOffice({
        id: budgetSubmission.institution,
        name: budgetSubmission.institution_name || ''
      });
      setSelectedFiscalYear({
        id: budgetSubmission.fiscal_year,
        name: budgetSubmission.fiscal_year_display || ''
      });

      const loadedRows = mapBudgetItemsToRows(budgetSubmission.budget_items || []);
      const fallbackRows = mapTemplateItemsToRows(searchedData?.fallback_items || []);
      setRows(mergeRowsWithTemplateRows(loadedRows, fallbackRows));

      if (budgetSubmission.token) {
        setLastSavedToken({
          token: budgetSubmission.token,
          institution: budgetSubmission.institution_name || '',
          createdAt: budgetSubmission.updated_at
            ? new Date(budgetSubmission.updated_at).toLocaleString('ne-NP')
            : new Date().toLocaleString('ne-NP')
        });
      } else {
        setLastSavedToken(null);
      }

      setViewMode(accessType === 'view');
      setLoadedFromTokenSearch(true);
      setActiveBudgetToken(budgetSubmission.token || '');
      setActiveSourceToken(searchedData.source_token || '');
      setShowOfficeDropdown(false);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!currentBudgetDraftKey || viewMode || loading) {
      return;
    }

    if (restoredDraftKeysRef.current.has(currentBudgetDraftKey)) {
      return;
    }
    restoredDraftKeysRef.current.add(currentBudgetDraftKey);

    try {
      const savedDraft = localStorage.getItem(currentBudgetDraftKey);
      if (!savedDraft) {
        return;
      }

      const parsedDraft = JSON.parse(savedDraft);
      const savedRows = Array.isArray(parsedDraft?.rows) ? parsedDraft.rows : [];
      if (savedRows.length === 0) {
        return;
      }

      setRows((prevRows) => mergeRowsWithTemplateRows(savedRows, prevRows));
      setMessage('स्थानीय ड्राफ्ट पुनर्स्थापना गरियो');
      setMessageType('success');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    } catch (storageError) {
      console.error('Failed to restore budget draft:', storageError);
    }
  }, [currentBudgetDraftKey, viewMode, loading]);

  useEffect(() => {
    if (!currentBudgetDraftKey || viewMode || loading) {
      return undefined;
    }

    if (skipNextDraftSaveRef.current) {
      skipNextDraftSaveRef.current = false;
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(currentBudgetDraftKey, JSON.stringify({
          rows,
          updated_at: new Date().toISOString()
        }));
      } catch (storageError) {
        console.error('Failed to save budget draft:', storageError);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [currentBudgetDraftKey, rows, viewMode, loading]);

  const clearCurrentBudgetDraft = useCallback(() => {
    if (!currentBudgetDraftKey) {
      return;
    }

    try {
      localStorage.removeItem(currentBudgetDraftKey);
    } catch (storageError) {
      console.error('Failed to clear budget draft:', storageError);
    }
  }, [currentBudgetDraftKey]);

  const handleOfficeDropdownToggle = () => {
    setShowOfficeDropdown((prev) => !prev);
  };

  const handleOfficeSelect = (office) => {
    setSelectedOffice({
      id: office.id,
      name: office.name
    });
    setShowOfficeDropdown(false);
    setOfficeSearchTerm('');
  };

  const handleInputChange = (rowId, field, value) => {
    setRows((prevRows) => prevRows.map((row) =>
      row.id === rowId
        ? {
            ...row,
            [field]: value,
            ...(field === 'codeNumber' ? { itemType: inferItemTypeFromCode(value) } : {})
          }
        : row
    ));
  };

  const addRow = (itemType = 'capital') => {
    setRows((prevRows) => [
      ...prevRows,
      {
        ...createEmptyRow((prevRows[prevRows.length - 1]?.id || 0) + 1),
        itemType
      }
    ]);
  };

  const removeRow = (rowId) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleReset = () => {
    if (viewMode) {
      showMessage('हेर्ने मोडमा रिसेट गर्न मिल्दैन', 'error');
      return;
    }

    if (loadedFromTokenSearch) {
      setRows(createDefaultRows());
      skipNextDraftSaveRef.current = true;
      clearCurrentBudgetDraft();
      showMessage('फर्म रिसेट गरियो', 'success');
      return;
    }

    setViewMode(false);
    setLoadedFromTokenSearch(false);
    setActiveBudgetToken('');
    setActiveSourceToken('');
    skipNextDraftSaveRef.current = true;
    clearCurrentBudgetDraft();
    fetchMasterItems();
    showMessage('फर्म रिसेट गरियो', 'success');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (viewMode) {
      showMessage('हेर्ने मोडमा सेभ गर्न मिल्दैन', 'error');
      return;
    }

    if (!selectedOffice?.id) {
      showMessage('कृपया संस्था छान्नुहोस्', 'error');
      return;
    }

    if (!selectedFiscalYear?.id) {
      showMessage('कृपया आर्थिक वर्ष छान्नुहोस्', 'error');
      return;
    }

    const items = rows
      .filter((row) => (
        row.codeNumber.trim() ||
        row.expenseTitle.trim() ||
        parseNumericValue(row.estimatedBudget) > 0 ||
        row.kaifiyat.trim()
      ))
      .map((row) => ({
        code_number: row.codeNumber.trim(),
        expense_title: row.expenseTitle.trim(),
        estimated_budget: parseNumericValue(row.estimatedBudget),
        remarks: row.kaifiyat.trim()
      }));

    if (items.length === 0) {
      showMessage('कृपया कम्तिमा एउटा बजेट पंक्ति भर्नुहोस्', 'error');
      return;
    }

    try {
      setLoading(true);
      const requestBody = {
        institution_id: selectedOffice.id,
        fiscal_year_id: selectedFiscalYear.id,
        items,
        ...(loadedFromTokenSearch && activeSourceToken ? { source_token: activeSourceToken } : {}),
        ...(loadedFromTokenSearch && activeBudgetToken && !activeSourceToken ? { token: activeBudgetToken } : {})
      };

      const response = await fetch(SAVE_BUDGET_API, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      if (result.status !== 'success') {
        showMessage(result.message || 'सेभ गर्न समस्या भयो', 'error');
        return;
      }

      const token = result?.data?.token;
      if (token) {
        setActiveBudgetToken(token);

        if (loadedFromTokenSearch && activeSourceToken) {
          setLastSavedToken({
            token: activeSourceToken,
            institution: selectedOffice.name,
            createdAt: new Date().toLocaleString('ne-NP')
          });
        } else {
          setTokenData({
            token,
            institution: selectedOffice.name,
            fiscalYear: selectedFiscalYear.name
          });
          setShowTokenPopup(true);
          setLastSavedToken({
            token,
            institution: selectedOffice.name,
            createdAt: new Date().toLocaleString('ne-NP')
          });
        }
      }

      skipNextDraftSaveRef.current = true;
      clearCurrentBudgetDraft();

      showMessage('बजेट डाटा सफलतापूर्वक सेभ भयो', 'success');
    } catch (error) {
      console.error('Error saving budget data:', error);
      showMessage('बजेट डाटा सेभ गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  const navigateByTab = (tabName) => {
    if (tabName === 'newData') {
      navigate('/capital-expenditure/new-data');
    } else if (tabName === 'budget') {
      navigate('/budjet');
    } else if (tabName === 'dataView') {
      navigate('/capital-expenditure/data-view');
    } else if (tabName === 'institutionManager') {
      navigate('/capital-expenditure/institution-manager');
    } else if (tabName === 'settings') {
      navigate('/capital-expenditure/settings');
    }
  };

  const capitalRows = sortBudgetRowsByCode(rows.filter((row) => {
    const itemType = row.itemType || inferItemTypeFromCode(row.codeNumber);
    return itemType === 'capital';
  }));

  const operationalRows = sortBudgetRowsByCode(rows.filter((row) => {
    const itemType = row.itemType || inferItemTypeFromCode(row.codeNumber);
    return itemType === 'operational';
  }));

  const totalCapitalBudget = capitalRows.reduce(
    (sum, row) => sum + parseNumericValue(row.estimatedBudget),
    0
  );

  const totalOperationalBudget = operationalRows.reduce(
    (sum, row) => sum + parseNumericValue(row.estimatedBudget),
    0
  );

  const totalBudget = totalCapitalBudget + totalOperationalBudget;
  const isTokenSession = loadedFromTokenSearch;

  const renderBudgetRow = (row, index) => (
    <tr key={row.id} className={styles.dataRow}>
      <td className={styles.codeCell}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ minWidth: '18px', fontWeight: 'bold' }}>{index + 1}.</span>
          <InputField
            value={row.codeNumber}
            onChange={(value) => handleInputChange(row.id, 'codeNumber', value)}
            placeholder="कोड नं."
            viewMode={viewMode}
            readOnly={viewMode}
          />
        </div>
      </td>
      <td className={styles.titleCell}>
        <InputField
          value={row.expenseTitle}
          onChange={(value) => handleInputChange(row.id, 'expenseTitle', value)}
          placeholder="खर्च शिर्षक"
          viewMode={viewMode}
          readOnly={viewMode}
        />
      </td>
      <td className={styles.budgetCell}>
        <InputField
          value={row.estimatedBudget}
          onChange={(value, englishValue) => handleInputChange(row.id, 'estimatedBudget', englishValue || value)}
          placeholder="०.००"
          viewMode={viewMode}
          readOnly={viewMode}
          isNumeric
        />
      </td>
      <td className={styles.budgetCell}>
        <InputField
          value={row.kaifiyat}
          onChange={(value) => handleInputChange(row.id, 'kaifiyat', value)}
          placeholder="कैफियत"
          viewMode={viewMode}
          readOnly={viewMode}
        />
      </td>
      <td className={styles.actionCell}>
        {!viewMode && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={() => removeRow(row.id)}
          >
            हटाउनुहोस्
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className={styles.capitalExpenditureContainer}>
      {showTokenPopup && (
        <div className={styles.tokenPopupOverlay}>
          <div className={styles.tokenPopup}>
            <div className={styles.tokenPopupHeader}>
              <div className={styles.tokenSuccessIcon}>✅</div>
              <h2>डाटा सफलतापूर्वक सेभ भयो!</h2>
              <p>कृपया निम्न टोकन सुरक्षित राख्नुहोस्:</p>
            </div>

            <div className={styles.tokenDisplay}>
              <div className={styles.tokenLabel}>आपको टोकन:</div>
              <div className={styles.tokenValue}>{tokenData?.token}</div>
              <div className={styles.tokenInfo}>
                <div className={styles.tokenInfoItem}>
                  <span className={styles.tokenInfoLabel}>संस्था:</span>
                  <span className={styles.tokenInfoValue}>{tokenData?.institution}</span>
                </div>
                <div className={styles.tokenInfoItem}>
                  <span className={styles.tokenInfoLabel}>आर्थिक वर्ष:</span>
                  <span className={styles.tokenInfoValue}>{tokenData?.fiscalYear}</span>
                </div>
              </div>
            </div>

            <div className={styles.tokenPopupActions}>
              <button
                className={styles.tokenCopyButton}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(tokenData?.token || '');
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                  } catch (error) {
                    console.error('Copy failed:', error);
                  }
                }}
              >
                {copySuccess ? '✅ कपी भयो!' : '📋 टोकन कपी गर्नुहोस्'}
              </button>
              <button
                className={styles.tokenCloseButton}
                onClick={() => setShowTokenPopup(false)}
              >
                बुझेको छु
              </button>
            </div>
          </div>
        </div>
      )}

      <CapitalExpenditureHeader
        activeTab="budget"
        setActiveTab={navigateByTab}
        selectedOffice={selectedOffice}
        viewMode={viewMode}
        lockSelection={isTokenSession}
        showOfficeDropdown={showOfficeDropdown}
        officeSearchTerm={officeSearchTerm}
        officeOptions={filteredOfficeOptions}
        lastSavedToken={lastSavedToken}
        message={message}
        messageType={messageType}
        handleOfficeDropdownToggle={handleOfficeDropdownToggle}
        handleOfficeSelect={handleOfficeSelect}
        setOfficeSearchTerm={setOfficeSearchTerm}
        selectedFiscalYear={selectedFiscalYear}
        setSelectedFiscalYear={setSelectedFiscalYear}
        fiscalYears={fiscalYears}
        onTabChange={navigateByTab}
        onRefreshClick={fetchMasterItems}
        onLogout={onLogout}
        isAuthenticated={isAuthenticated}
      />

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit}>
          <div className={styles.tableContainer}>
            <table className={styles.expenditureTable}>
              <thead>
                <tr className={styles.mainHeader}>
                  <th className={styles.codeHeader}>कोड नं.</th>
                  <th className={styles.titleHeader}>खर्च शिर्षक</th>
                  <th className={styles.budgetHeader}>अनुमानित बजेट</th>
                  <th className={styles.budgetHeader}>कैफियत</th>
                  <th className={styles.actionCell}>कार्य</th>
                </tr>
              </thead>
              <tbody>
                <tr className={styles.categoryRow}>
                  <td colSpan="5" className={styles.categoryCell}>पुँजीगत खर्च</td>
                </tr>

                {capitalRows.map((row, index) => renderBudgetRow(row, index))}

                <tr className={styles.totalRow}>
                  <td colSpan="2">जम्मा पुँजीगत</td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={totalCapitalBudget}
                      readOnly
                      viewMode
                      isNumeric
                    />
                  </td>
                  <td className={styles.budgetCell}></td>
                  <td className={styles.actionCell}></td>
                </tr>

                <tr className={styles.categoryRow}>
                  <td colSpan="5" className={styles.categoryCell}>संचालन खर्च</td>
                </tr>

                {operationalRows.map((row, index) => renderBudgetRow(row, index))}

                <tr className={styles.totalRow}>
                  <td colSpan="2">जम्मा संचालन</td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={totalOperationalBudget}
                      readOnly
                      viewMode
                      isNumeric
                    />
                  </td>
                  <td className={styles.budgetCell}></td>
                  <td className={styles.actionCell}></td>
                </tr>

                <tr className={styles.totalRow}>
                  <td colSpan="2">जम्मा कुल बजेट</td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={totalBudget}
                      readOnly
                      viewMode
                      isNumeric
                    />
                  </td>
                  <td className={styles.budgetCell}></td>
                  <td className={styles.actionCell}></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.addRowButton}
              onClick={() => addRow('capital')}
              disabled={viewMode}
            >
              पुँजीगत पंक्ति थप्नुहोस्
            </button>
            <button
              type="button"
              className={styles.addRowButton}
              onClick={() => addRow('operational')}
              disabled={viewMode}
            >
              संचालन पंक्ति थप्नुहोस्
            </button>
            <button
              type="button"
              className={styles.refreshButton}
              onClick={loadExistingBudgetData}
              disabled={loading || !selectedOffice?.id || !selectedFiscalYear?.id}
            >
              {loading ? 'लोड हुँदैछ...' : 'सेभ भएको डाटा लोड गर्नुहोस्'}
            </button>
            {!viewMode && (
              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading}
              >
                {loading ? 'सेभ हुँदैछ...' : 'बजेट सेभ गर्नुहोस्'}
              </button>
            )}
            <button
              type="button"
              className={styles.resetButton}
              onClick={handleReset}
              disabled={loading}
            >
              रिसेट गर्नुहोस्
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetMain;
